import React, { useCallback, useEffect, useMemo } from "react";
import { Redirect, Route, Switch } from "react-router";
import { Routes } from "../constants/Routes";
import Layout from "../layouts/MainLayout";
import { useShallowEqualSelector } from "../hooks/useShallowSelector";
import LoginPage from "../components/pages/auth/LoginPage";
import { useHistory } from "react-router-dom";
import ForgetPasswordPage from "../components/pages/auth/ForgetPassword";
import {
  authDateExpiredSelector,
  authDateSelector,
  refreshTokenSelector,
  setAuthDateExpired,
  setRefreshToken,
  setToken,
  tokenSelector,
  tokenTimeSelector,
} from "../reducers/AuthReducer";
import { useDispatch } from "react-redux";
import { useAuthContext } from "../api/auth/AuthContext";
import { Suspense } from "react";
import Loader from "../components/loader/Loader";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { userInformationSelector } from "../reducers/UserReducer";
import NotFoundContainer from "./NotFoundContainer";
import { RolesEnum } from "../api/AppDTO";

const MonitoringContainer = React.lazy(() => import("./MonitoringContainer"));
const ProductsContainer = React.lazy(() => import("./ProductsContainer"));
const ShipmentContainer = React.lazy(() => import("./ShipmentContainer"));
const StaffContainer = React.lazy(() => import("./StaffContainer"));
const SettingContainer = React.lazy(() => import("./SettingContainer"));

function RootContainer() {
  const history = useHistory();
  const token = useShallowEqualSelector(tokenSelector);
  const userInformation = useShallowEqualSelector(userInformationSelector);
  const dispatch = useDispatch();
  const { AuthApi, logout } = useAuthContext();
  const tokenTime = useShallowEqualSelector(tokenTimeSelector);
  const refresh_token = useShallowEqualSelector(refreshTokenSelector);
  const authDate = useShallowEqualSelector(authDateSelector);
  const authExpired = useShallowEqualSelector(authDateExpiredSelector);
  const isAuthorized = useMemo(() => Boolean(token), [token]);
  const reGetToken = useCallback(() => {
    AuthApi.refreshAccessToken({ refresh_token: refresh_token })
      .then((res) => {
        if (res) {
          dispatch(setToken({ token: res.access_token }));
          dispatch(setRefreshToken({ refreshToken: res.refresh_token }));
        }
      })
      .catch(() => {
        return logout && logout();
      });
  }, [refresh_token, dispatch, AuthApi, logout]);
  useEffect(() => {
    if ((!isAuthorized || authExpired) && logout) {
      history.push(Routes.LoginPage);
      logout();
    } else {
      if (window.location.pathname === Routes.LoginPage) {
        history.push(Routes.Monitoring);
      }
    }
  }, [isAuthorized, history, logout, authExpired]);
  useEffect(() => {
    if (authDate) {
      //@ts-ignore
      if ((Date.now() - authDate) / 1000 > tokenTime + 40) {
        reGetToken();
        //@ts-ignore
      } else if ((Date.now() - authDate) / 1000 >= tokenTime - 5) {
        dispatch(setAuthDateExpired({ state: true }));
      }
    }
  }, [authDate, tokenTime, reGetToken, dispatch]);
  useVersionCheck();
  return (
    <>
      {isAuthorized ? (
        <Layout>
          <Suspense
            fallback={
              <div className="d-flex flex-1 align-items-center justify-content-center">
                <div>
                  <Loader />
                </div>
              </div>
            }
          >
            <Switch>
              <Redirect to={Routes.Monitoring} from="/" exact={true} />
              <Route path={Routes.Monitoring} component={MonitoringContainer} />
              <Route path={Routes.Products} component={ProductsContainer} />
              {userInformation?.roleName !== RolesEnum.Loader && (
                <Route path={Routes.Shipment} component={ShipmentContainer} />
              )}
              {userInformation?.roleName === RolesEnum.Chief && (
                <Route path={Routes.Staff} component={StaffContainer} />
              )}
              {userInformation?.roleName === RolesEnum.Chief && (
                <Route path={Routes.Settings} component={SettingContainer} />
              )}
              <Route component={NotFoundContainer} />
            </Switch>
          </Suspense>
        </Layout>
      ) : (
        <Switch>
          {/*<Redirect to={Routes.LoginPage} from={window.location.pathname} />*/}
          <Route path={Routes.LoginPage} component={LoginPage} />
          <Route path={Routes.ForgetPassword} component={ForgetPasswordPage} />
        </Switch>
      )}
    </>
  );
}

export default RootContainer;
